import type { ApiClient } from "@authe/fe-commons/api";
import { CATALOG_ITEM_KINDS, CATALOG_PRIMARY_RESOURCE, catalogResource } from "@authe/fe-commons/catalog";
import type { ResourceChangeHub } from "@authe/fe-commons/events";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { queryDependsOnResources } from "./queryResourcePolicies";
import { catalogStreamFor } from "./catalogLive";

export interface ResourceCacheBinding {
  resource: string;
  stream?: string;
  requestPaths: readonly string[];
  matchesQuery: (key: QueryKey) => boolean;
}

/** Add future resource adapters here; transport and lifecycle need no changes. */
export const resourceCacheBindings: readonly ResourceCacheBinding[] = [
  ...CATALOG_ITEM_KINDS.map((kind) => ({
    resource: catalogResource(kind),
    stream: catalogStreamFor(kind),
    // Also evict assignment/primary reads affected by chain applicability.
    requestPaths: ["/catalog", "/addresses", "/transactions", "/seed-reviews"],
    matchesQuery: (key: QueryKey) => queryDependsOnResources(key, new Set([catalogResource(kind)])),
  })),
  { resource: CATALOG_PRIMARY_RESOURCE, requestPaths: ["/catalog-primary", "/addresses", "/transactions", "/seed-reviews"],
    matchesQuery: (key: QueryKey) => queryDependsOnResources(key, new Set([CATALOG_PRIMARY_RESOURCE])) },
];

/** Framework-specific cache adapter; the hub itself has no TanStack dependency. */
export function connectResourceCaches(
  hub: ResourceChangeHub, api: Pick<ApiClient, "invalidateMatching">, queries: QueryClient,
  bindings: readonly ResourceCacheBinding[] = resourceCacheBindings,
): () => void {
  const enabled = bindings.filter((binding) => hub.resources.has(binding.resource));
  let active = true;
  const clearRequests = (resources: ReadonlySet<string>) => {
    const paths = new Set(enabled.filter((b) => resources.has(b.resource)).flatMap((b) => b.requestPaths));
    for (const path of paths) api.invalidateMatching(path);
  };
  const offImmediate = hub.subscribe(({ resource }) => clearRequests(new Set([resource])));
  const offBatched = hub.subscribeBatched((resources) => {
    const affected = enabled.filter((b) => resources.has(b.resource));
    const filters = { predicate: (query: { queryKey: QueryKey }) => affected.some((b) => b.matchesQuery(query.queryKey)) };
    // Cancel even initial reads: TanStack's invalidate alone can join their
    // pre-mutation result when no previous data exists.
    void queries.cancelQueries(filters).then(() => {
      if (active) return queries.invalidateQueries({ ...filters, refetchType: "active" });
    }).catch(() => { /* Query observers expose read errors and retries. */ });
  });
  return () => {
    if (!active) return;
    active = false;
    offImmediate();
    offBatched();
    clearRequests(hub.resources);
    const filters = { predicate: (query: { queryKey: QueryKey }) => enabled.some((b) => b.matchesQuery(query.queryKey)) };
    void queries.cancelQueries(filters);
    queries.removeQueries(filters);
  };
}
